import { useClientPortalAPI } from "@certn/api";
import {
  AvatarWithName,
  ColumnDefinition,
  DataTable,
  Link,
  Stack,
  Text,
  mq,
} from "@certn/cascadia-react";
import {
  sortDescriptorToOrderingParam,
  orderingParamToSortDescriptor,
} from "@certn/cascadia-react/components/data-tables/utils";
import { formatDate, parseName } from "@certn/utils";
import { css } from "@emotion/css";
import { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useParams } from "react-router-dom";

import { ActionsDropdown } from "./ActionsDropdown";
import { CreateAPIKeyButton } from "./CreateAPIKeyButton";
import { CreateAPIKeyModal } from "./CreateAPIKeyModal";
import { Empty } from "./Empty";
import { useQueryParams } from "../../../../hooks/useQueryParams";
import { Subtitle } from "../../Subtitle";

export function APIKeyList() {
  const { t } = useTranslation();
  const { apiConfigurationID = "" } = useParams();
  const { params, setParam } = useQueryParams();

  const ordering = params.ordering || "-created";

  const [isCreateAPIKeyModalOpen, onCreateAPIKeyModalOpenChange] =
    useState(false);
  const onOpenCreateAPIKeyModal = () => onCreateAPIKeyModalOpenChange(true);

  const { data, isLoading, error, mutate } = useClientPortalAPI(
    "/api/internal/client-portal/public-api/api-keys/",
    {
      public_api_configuration: apiConfigurationID,
      page_size: 100, // Note: no api config can have more than 100 API keys
      ...params,
    }
  );

  const rows = data?.results || [];

  const columns: ColumnDefinition<(typeof rows)[number]>[] = [
    {
      key: "name",
      name: t("Name"),
      isRowHeader: true,
      testID: "name-column-header",
      render(item) {
        return (
          <Text className={styles.noWrapMobile} testID="integration-name">
            {item.name}
          </Text>
        );
      },
    },
    {
      key: "api_key",
      name: t("API Key"),
      render(item) {
        return (
          <Text className={styles.noWrapMobile} testID="integration-api-key">
            {item.prefix}*************************
          </Text>
        );
      },
    },
    {
      key: "created",
      name: t("Created"),
      allowsSorting: true,
      render(item) {
        return (
          <Text className={styles.noWrapMobile} testID="integration-created">
            {formatDate(item.created, "MMMM d, yyyy")}
          </Text>
        );
      },
    },
    {
      key: "expiry_date",
      name: t("Expiry"),
      render(item) {
        return (
          <Text
            className={styles.noWrapMobile}
            testID="integration-expiry-date"
          >
            {formatDate(item.expiry_date, "MMMM d, yyyy")}
          </Text>
        );
      },
    },
    {
      key: "created_by",
      name: t("Created by"),
      render(item) {
        return (
          <AvatarWithName
            name={parseName(item.created_by?.auth0_full_name)}
            subtitle={item.created_by?.auth0_email}
            testID="integration-created-by"
          />
        );
      },
    },
    {
      key: "actions",
      isAction: true,
      render(apiKey) {
        return <ActionsDropdown apiKey={apiKey} onItemChanged={mutate} />;
      },
      width: 48,
    },
  ];

  return (
    <Stack gap="space-6">
      <Subtitle
        subtitle={t("API Keys")}
        description={
          <Trans>
            Develop your own custom integration to access Certn's public API.{" "}
            <Link
              testID="developer-api-learn-more"
              href={`/settings/integrations/${apiConfigurationID}/documentation#authentication`}
              linkStyle="monochrome"
            >
              Learn more
            </Link>
          </Trans>
        }
      >
        <CreateAPIKeyButton onPress={onOpenCreateAPIKeyModal} />
      </Subtitle>

      {error || isLoading || rows.length > 0 ? (
        <DataTable
          isLoading={isLoading}
          testId="api-key-table"
          columns={columns}
          rows={rows}
          onSortChange={(sortDescriptor) =>
            setParam("ordering", sortDescriptorToOrderingParam(sortDescriptor))
          }
          renderEmptyState={() =>
            error && (
              <Text color="text-danger">
                {t("Something went wrong when loading API keys.")}
              </Text>
            )
          }
          sortDescriptor={orderingParamToSortDescriptor(ordering)}
          aria-label="api-key-table"
        />
      ) : (
        <Empty onOpenCreateAPIKeyModal={onOpenCreateAPIKeyModal} />
      )}
      <CreateAPIKeyModal
        apiConfigurationID={apiConfigurationID}
        onCreated={mutate}
        isOpen={isCreateAPIKeyModalOpen}
        onOpenChange={onCreateAPIKeyModalOpenChange}
      />
    </Stack>
  );
}

const styles = {
  noWrapMobile: css(
    mq({
      whiteSpace: ["nowrap", "normal"],
    })
  ),
};
